import React, { createContext, useState, useContext, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";

const EventParticipationSubmissionContext = createContext();

export const useEventParticipationSubmission = () => {
    const context = useContext(EventParticipationSubmissionContext);
    if (!context) {
        throw new Error("useEventParticipationSubmission must be used within an EventParticipationSubmissionProvider");
    }
    return context;
};

export function EventParticipationSubmissionProvider({ children, eventId }) {
    const [formData, setFormData] = useState({
        address: "",
        postalCode: "",
        city: "",
        country: "",
        
        manufacturer: "",
        model: "",
        buildYear: "",
        licensePlate: "",
        horsePower: "",
        
        vehicleImageId: null,
        vehicleImageUrl: null,
        vehicleImage: null,

        customFields: {},
    });
    
    const [errors, setErrors] = useState({});
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [existingSubmissions, setExistingSubmissions] = useState(null);
    const [event, setEvent] = useState(null);

    const fetchEvent = useCallback(async () => {
        setErrors(prev => ({ ...prev, event: null }));
        try {
            const response = await axios.post("/getEvent", { eventId });
            setEvent(response.data.event);
        } catch (requestError) {
            console.error("Error fetching event:", requestError);
            setErrors(prev => ({ ...prev, event: "Er is een fout opgetreden bij het laden van het evenement. Probeer het later opnieuw." }));
        }
    }, [eventId]);

    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);

    const fetchExistingSubmissions = useCallback(async () => {
        try {
            const response = await axios.post("/getUserEventParticipation", { eventId });
            setExistingSubmissions(response.data.submission);
        } catch (requestError) {
            console.error("Error fetching existing submissions:", requestError);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchExistingSubmissions();
    }, [fetchExistingSubmissions]);

    const fetchCustomFields = useCallback(async () => {
        setFields([]);
        setLoading(true);
        try {
            const response = await axios.post("/getEventParticipationFields", { eventId });
            setFields(response.data.fields);
        } catch (requestError) {
            console.error("Error fetching custom fields:", requestError);
        } finally {
            setLoading(false);
        }
    }, [eventId]);

    useEffect(() => {
        fetchCustomFields();
    }, [fetchCustomFields]);
    
    const updateFormData = useCallback((field, value) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    }, []);
    
    const updateCustomField = useCallback((fieldId, value) => {
        setFormData(prevData => ({
            ...prevData,
            customFields: {
                ...prevData.customFields,
                [fieldId]: value
            }
        }));
    }, []);
    
    const submitForm = useCallback(async () => {
        try {
            const formattedData = {
                eventId,
                ...formData,
                customFields: formData.customFields || {}
            };

            const response = await axios.post("/submitEventParticipation", formattedData);
            console.log("Response:", response);

            setFormData({
                address: "",
                postalCode: "",
                city: "",
                country: "",
                manufacturer: "",
                model: "",
                buildYear: "",
                licensePlate: "",
                horsePower: "",
                customFields: {},
            });
            
            return true;
        } catch (error) {
            console.error("Error submitting form:", error);
            setErrors({ submit: "Er ging iets mis bij het indienen van het formulier. Probeer het later opnieuw." });
            return false;
        }
    }, [eventId, formData]);
    
    const value = {
        event,
        formData,
        errors,
        updateFormData,
        updateCustomField,
        submitForm,
        customFields : fields,
        loading,
        existingSubmissions,
    };
    
    return (
        <EventParticipationSubmissionContext.Provider value={value}>
            {children}
        </EventParticipationSubmissionContext.Provider>
    );
}

EventParticipationSubmissionProvider.propTypes = {
    children: PropTypes.node.isRequired,
    eventId: PropTypes.string.isRequired,
    event: PropTypes.object
};

export default EventParticipationSubmissionProvider; 
